<script lang="ts" setup>
import { PATH_CARTOGRAPHY } from "./modules/cartography/constants";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import en from "element-plus/es/locale/lang/en";
import es from "element-plus/es/locale/lang/es";
import fr from "element-plus/es/locale/lang/fr";
import it from "element-plus/es/locale/lang/it";
import { useConfigStore } from "~/core/stores/useConfigStore";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";

const { getConfig } = useConfigStore();

const route = useRoute();
const { locale, t } = useI18n();
const userSessionStore = useUserSession();

const elementPlusLangs: Record<string, object> = {
  fr,
  en,
  it,
  es,
};

const shouldVersionBeDisplayed = computed(() => route.path != PATH_CARTOGRAPHY);

const optionalLabel = computed(() => t("input.optional"));
provide("lxc-optional-label", optionalLabel);

const updateLanguage = () => {
  const currentLocale = userSessionStore.userSession?.language ?? locale.value;
  locale.value = currentLocale;
  dayjs.locale(currentLocale);
};

/**
 * Start managing display confirm message on closing/reload window and on leaving page
 */
const confirmLeavePageStore = useConfirmLeavePageStore();

// Display confirm message on refresh/close window
window.addEventListener("beforeunload", (event) => {
  if (confirmLeavePageStore.isActivated()) {
    event.returnValue = confirmLeavePageStore.messageConfirmUnsavedData;
  }
});

// show confirm message component based on the confirmLeavePageStore "shown" state
const showConfirm = computed({
  get(): boolean {
    return confirmLeavePageStore.$state.shown;
  },
  set(showConfirm: boolean) {
    confirmLeavePageStore.$state.shown = showConfirm;
  },
});
/**
 * End managing display confirm message on closing/reload window and on leaving page
 */

// Init after user session has changed
watch(
  () => userSessionStore.userSession,
  () => {
    // Case when the user is authenticated
    if (userSessionStore.isAuthenticated) {
      updateLanguage();
    }
  },
);

const onCancelLeavePageconfirm = () => {
  confirmLeavePageStore.cancel();
  showConfirm.value = false;
};

// Warning: the user can be not already authenticated here
onBeforeMount(updateLanguage);
onMounted(getConfig);
</script>

<template>
  <el-config-provider :locale="elementPlusLangs[locale] || elementPlusLangs.en">
    <router-view />
    <version-component v-if="shouldVersionBeDisplayed" />
  </el-config-provider>

  <!-- Leave page confirm component -->
  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t(confirmLeavePageStore.title)"
    :description="t(confirmLeavePageStore.messageConfirmUnsavedData)"
    :ok-label="t('message.confirm.okLabel')"
    :cancel-label="t('message.confirm.cancelLabel')"
    @confirm="confirmLeavePageStore.confirm"
    @cancel="onCancelLeavePageconfirm"
  />
</template>
